if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements = {};
        this.onSlideChangedHandler = debounce(this.onSlideChanged.bind(this), 500);
        this.onThumbnailClickHandler = this.onThumbnailClick.bind(this);
        this.onPageShowHandler = this.refresh.bind(this);
      }

      connectedCallback() {
        this.refresh();
        window.addEventListener('pageshow', this.onPageShowHandler);
      }

      disconnectedCallback() {
        this.removeEventListeners();
        window.removeEventListener('pageshow', this.onPageShowHandler);
      }

      refresh() {
        this.removeEventListeners();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', this.onSlideChangedHandler);
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.addEventListener('click', this.onThumbnailClickHandler);
        }

        if (this.isDesktopThumbnailLayout()) this.removeListSemantic();
      }

      removeEventListeners() {
        this.elements.viewer?.removeEventListener('slideChanged', this.onSlideChangedHandler);
        this.elements.thumbnails?.removeEventListener('click', this.onThumbnailClickHandler);
      }

      onThumbnailClick(event) {
        const thumbnail = event.target.closest('[data-target]');
        if (!thumbnail || !this.elements.thumbnails?.contains(thumbnail)) return;

        event.preventDefault();
        thumbnail.querySelector('button')?.focus({ preventScroll: true });
        this.setActiveMedia(thumbnail.dataset.target, false);
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.isDesktopThumbnailLayout()) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          if (this.isDesktopThumbnailLayout()) {
            const targetTop = activeMedia.getBoundingClientRect().top + window.scrollY;
            const stickyHeaderOffset = this.getStickyHeaderOffset();
            window.scrollTo({
              top: Math.max(targetTop - stickyHeaderOffset - 16, 0),
              behavior: 'smooth',
            });
            return;
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      getStickyHeaderOffset() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        return this.stickyHeader?.offsetHeight || 0;
      }

      isDesktopThumbnailLayout() {
        return this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches;
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
